import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DetailedTypeMaster, CategoryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const DetailedTypeMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [detailedTypes, setDetailedTypes] = useState<DetailedTypeMaster[]>([]);
  const [categories, setCategories] = useState<CategoryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDetailedType, setEditingDetailedType] = useState<DetailedTypeMaster | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    applicableCategories: [] as string[],
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCategories();
    fetchDetailedTypes();
  }, []);

  const fetchCategories = async () => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'categories' });
      const data = (result.data as any).data as CategoryMaster[];
      setCategories(data);
    } catch (error: any) {
      onError(error.message || '業種マスタの取得に失敗しました');
    }
  };

  const fetchDetailedTypes = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'detailedTypes', includeInactive: true });
      const data = (result.data as any).data as DetailedTypeMaster[];
      setDetailedTypes(data);
    } catch (error: any) {
      onError(error.message || '店舗タイプマスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (detailedType?: DetailedTypeMaster) => {
    if (detailedType) {
      setEditingDetailedType(detailedType);
      setFormData({
        code: detailedType.code,
        nameJa: detailedType.name.ja,
        nameTh: detailedType.name.th || '',
        nameEn: detailedType.name.en || '',
        applicableCategories: detailedType.applicableCategories,
        sortOrder: detailedType.sortOrder,
        isActive: detailedType.isActive,
      });
    } else {
      setEditingDetailedType(null);
      setFormData({
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        applicableCategories: [],
        sortOrder: detailedTypes.length + 1,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDetailedType(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.code || !formData.nameJa || formData.applicableCategories.length === 0) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingDetailedType) {
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingDetailedType.id,
          type: 'detailedTypes',
          data: {
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            applicableCategories: formData.applicableCategories,
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('店舗タイプマスタを更新しました');
      } else {
        const createDetailedType = httpsCallable(functions, 'createDetailedType');
        await createDetailedType({
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          applicableCategories: formData.applicableCategories,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('店舗タイプマスタを作成しました');
      }

      handleCloseDialog();
      fetchDetailedTypes();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (detailedType: DetailedTypeMaster) => {
    if (!window.confirm(`${detailedType.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      const result = await deleteMasterData({
        id: detailedType.id,
        type: 'detailedTypes',
      });
      onSuccess((result.data as any).message || '店舗タイプマスタを削除しました');
      fetchDetailedTypes();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  const getCategoryNames = (categoryIds: string[]) => {
    return categoryIds
      .map(id => {
        const category = categories.find(c => c.id === id);
        return category ? `${category.icon} ${category.name.ja}` : id;
      })
      .join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          店舗タイプを追加
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>適用可能な業種</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detailedTypes.map((detailedType) => (
              <TableRow key={detailedType.id}>
                <TableCell>{detailedType.code}</TableCell>
                <TableCell>{detailedType.name.ja}</TableCell>
                <TableCell>{detailedType.name.th || '-'}</TableCell>
                <TableCell>{detailedType.name.en || '-'}</TableCell>
                <TableCell>{getCategoryNames(detailedType.applicableCategories)}</TableCell>
                <TableCell>{detailedType.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={detailedType.isActive ? '有効' : '無効'}
                    color={detailedType.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(detailedType)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(detailedType)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDetailedType ? '店舗タイプマスタを編集' : '店舗タイプマスタを追加'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingDetailedType}
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>適用可能な業種</InputLabel>
            <Select
              multiple
              value={formData.applicableCategories}
              onChange={(e) => setFormData({ ...formData, applicableCategories: e.target.value as string[] })}
              input={<OutlinedInput label="適用可能な業種" />}
              renderValue={(selected) => getCategoryNames(selected)}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Checkbox checked={formData.applicableCategories.indexOf(category.id) > -1} />
                  <ListItemText primary={`${category.icon} ${category.name.ja}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DetailedTypeMasterTab;
